import '@farmshare/ui-components/styles.scss'; // Import this first, as it includes the globals, so with matching specificity these are overriding other classes
import {
  Error,
  FullscreenLoading,
  ModalProvider,
  MuiSwitchableThemeProvider,
  ToastrProvider,
} from '@farmshare/ui-components';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { includes } from 'lodash';
import { ComponentType, lazy, StrictMode, Suspense, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  Link,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
} from 'react-router-dom';
import { RecoilRoot } from 'recoil';

import { AuthApolloProvider } from 'components/auth-apollo-provider/auth-apollo-provider';
import { afterPageLoad } from 'lib/after-page-load';
import { trackModalEvent } from 'lib/analytics';
import { PageViewTracker } from 'lib/use-page-view-tracking';
import {
  createTracesSampler,
  FARMSHARE_URL,
  IGNORED_RESOURCE_SPANS,
} from 'lib/sentry-trace-sampling';
import { importOptional, isOptionalImportPending } from 'lib/optional-import';
import {
  captureSmsRecipientPhone,
  CognitoAuthContext,
  CognitoAuthProvider,
} from 'features/auth';
import {
  initializeModules,
  ModuleProtectedRoute,
  moduleRegistry,
} from 'lib/modules';

import { EnumUserRole } from './lib/graphql';
import {
  isBuyerOnly,
  isProcessor,
  isProducer,
  PROCESSOR_VIEW_ROLES,
} from 'lib/userHelpers';

import { App } from './app';
import logo from './assets/images/partners.svg';
import { RouterErrorBoundary } from './router-error-boundary';
import { UserStateProvider, useUserState } from 'providers/user-state-provider';
import { SettingsProvider } from 'providers';

// Initialize all application modules
initializeModules();

// Recover from stale dynamic-import / CSS chunks after a deploy.
//
// Vite emits a `vite:preloadError` event when a lazily-imported chunk (or its
// CSS) fails to load — almost always because a new deploy replaced the hashed
// asset filenames while a user still holds an old index.html. Reloading fetches
// the fresh index + assets. We cap auto-reloads to once per 10s so a genuinely
// missing asset can't trigger an infinite reload loop.
//
// `sessionStorage` access can throw in sandboxed iframes, Safari private mode,
// or when storage is disabled. Wrap each access so a storage failure still
// falls through to a plain reload rather than swallowing the error.
//
// Chunks loaded through `importOptional` are skipped: the page works without
// them, so a failure there is not worth interrupting the user for.
window.addEventListener('vite:preloadError', () => {
  if (isOptionalImportPending()) return;
  const RELOAD_KEY = 'vite-preload-error-reloaded-at';
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    // Storage unavailable — fall through and reload (loop guard disabled).
  }
  if (Date.now() - lastReloadAt < 10_000) {
    // Already reloaded very recently — let the error surface instead of looping.
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — proceed without the loop guard.
  }
  window.location.reload();
});

Sentry.init({
  dsn: 'https://385f52358dc9796cfecdecab3e87c5ae@o4509940188512257.ingest.us.sentry.io/4509944746475520',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.VITE_ENV,
  enabled: !!import.meta.env.VITE_ENV, // Is this running a built version? Or is it live reloading?
  integrations: [
    // Add browser profiling integration to the list of integrations
    Sentry.browserProfilingIntegration(),
    Sentry.browserTracingIntegration({
      // No span per downloaded file — see `IGNORED_RESOURCE_SPANS`.
      ignoreResourceSpans: IGNORED_RESOURCE_SPANS,
    }),
    // Forward `console.error` calls to Sentry so intentional error logs in
    // catch blocks (which already toast the user) are also visible in
    // observability rather than only in the dev console.
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    // Session replay is added after the page has loaded — see below.
  ],
  // Page loads 5%, navigations 1%, dev and staging not at all — see
  // `traceSampleRate`.
  tracesSampler: createTracesSampler(import.meta.env.VITE_ENV),
  // The API follows the browser's decision for the requests it makes, so in
  // dev and staging those API traces aren't recorded either, and in
  // production they are kept at the browser's rate.
  tracePropagationTargets: ['localhost', FARMSHARE_URL],
  profilesSampleRate: 0.1, // Relative to the trace rate: 0.5% of production page loads and 0.1% of navigations; nothing in dev or staging.
  replaysSessionSampleRate: 0.0001, // Let's sample 0.1% of sessions for replay
  replaysOnErrorSampleRate: 1, // When an error happens, let's replay 2% of sessions
});

// Session replay is the largest single piece of Sentry's code and isn't needed
// to get the app on screen, so it is fetched once the page has loaded. The
// replay sample rates above still apply. The trade-off: an error in the first
// few seconds of a session has no replay attached.
if (import.meta.env.VITE_ENV) {
  afterPageLoad(() => {
    void importOptional(() => import('lib/sentry-deferred-integrations')).then(
      (deferred) => {
        if (deferred) Sentry.addIntegration(deferred.replayIntegration());
      },
    );
  });
}

// Lazy-loaded page components with Vite chunk names
const AdminPage = lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/admin'),
);
const AdminAssetPage = lazy(
  () => import(/* webpackChunkName: "admin-assets" */ 'pages/admin/assets'),
);
const AdminHangingWeightExportPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-hanging-export" */ 'pages/admin/hanging-export'
    ),
);
const AdminJobExportPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-job-export" */ 'pages/admin/job-export'),
);
const AdminProcessorExportPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-processor-export" */ 'pages/admin/processor-export'
    ),
);
const AdminShipmentPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-shipments" */ 'pages/admin/shipments'),
);
const AdminTenantPage = lazy(
  () => import(/* webpackChunkName: "admin-tenants" */ 'pages/admin/tenants'),
);
const AdminUsersPage = lazy(
  () => import(/* webpackChunkName: "admin-users" */ 'pages/admin/users'),
);
const AdminTenantDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-tenant-details" */ 'pages/admin/tenants/details'
    ),
);
const AdminAuditTrailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-audit-trail" */ 'pages/admin/audit-trail'
    ),
);
const AdminStatsPage = lazy(
  () => import(/* webpackChunkName: "admin-stats" */ 'pages/admin/stats'),
);
const AdminAccountActivityPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-account-activity" */ 'pages/admin/account-activity'
    ),
);
const AdminNotificationAuditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-notification-audit" */ 'pages/admin/notification-audit'
    ),
);
const AdminMarketplaceLeadsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-marketplace-leads" */ 'pages/admin/marketplace-leads'
    ),
);
const AdminLandingPagesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-landing-pages" */ 'pages/admin/landing-pages'
    ),
);
const AdminLandingPageDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-landing-page-details" */ 'pages/admin/landing-pages/details'
    ),
);
const AdminCutsheetLibrariesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-cutsheet-libraries" */ 'pages/admin/cutsheet-libraries'
    ),
);
const FAQPage = lazy(() => import(/* webpackChunkName: "faq" */ 'pages/faq'));
const ComparisonTool = lazy(
  () =>
    import(
      /* webpackChunkName: "comparison" */ 'components/comparison-tool/comparison-tool'
    ),
);
const HomePage = lazy(
  () => import(/* webpackChunkName: "home" */ 'pages/home'),
);
// The buyer marketplace is switched off (PRO-784): `/marketplace` and
// `/marketplace/search` serve only the processor finder now.
const ProcessFinderPage = lazy(
  () =>
    import(
      /* webpackChunkName: "process-finder" */ 'features/buyer-marketplace/marketplace/process-finder'
    ),
);
const ProcessSearchPage = lazy(
  () =>
    import(
      /* webpackChunkName: "process-search" */ 'features/buyer-marketplace/marketplace/process-search'
    ),
);
const IntegrationsPage = lazy(
  () => import(/* webpackChunkName: "integrations" */ 'pages/integrations'),
);
const CognitoLoginPage = lazy(
  () =>
    import(
      /* webpackChunkName: "cognito-login" */ 'features/auth/pages/cognito-login'
    ),
);
const AuthCallbackPage = lazy(
  () => import(/* webpackChunkName: "auth-callback" */ 'pages/auth-callback'),
);
const EditSplitType = lazy(() =>
  import(
    /* webpackChunkName: "edit-split-type" */ 'pages/processing-job/_views/edit-split-type'
  ).then((m) => ({ default: m.EditSplitType })),
);
const AnimalHead = lazy(() =>
  import(
    /* webpackChunkName: "animal-head" */ 'pages/processing-job/animal-head'
  ).then((m) => ({ default: m.AnimalHead })),
);
const AddAnimalHead = lazy(() =>
  import(
    /* webpackChunkName: "add-animal-head" */ 'pages/processing-job/animal-head-add'
  ).then((m) => ({ default: m.AddAnimalHead })),
);
const GenerateInvoice = lazy(() =>
  import(
    /* webpackChunkName: "generate-invoice" */ 'pages/processing-job/generate-invoice'
  ).then((m) => ({ default: m.GenerateInvoice })),
);
const ProcessingJob = lazy(() =>
  import(
    /* webpackChunkName: "processing-job" */ 'pages/processing-job/processing-job'
  ).then((m) => ({ default: m.ProcessingJob })),
);
const ProcessorAdminPage = lazy(
  () => import(/* webpackChunkName: "processor-admin" */ 'pages/processor'),
);
const ProcessorCustomerDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "processor-customer-details" */ 'features/customers/components/details'
    ),
);
const Waitlist = lazy(() =>
  import(
    /* webpackChunkName: "waitlist" */ 'pages/processor/scheduling-settings/_views/waitlist'
  ).then((m) => ({ default: m.Waitlist })),
);
const ChatPage = lazy(
  () => import(/* webpackChunkName: "chat" */ 'pages/producer/chat'),
);
const ProducerJobsPage = lazy(() =>
  import(
    /* webpackChunkName: "producer-jobs" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.ProducerJobsPage })),
);
const ProducerDashboard = lazy(() =>
  import(
    /* webpackChunkName: "producer-dashboard" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.DashboardRouter })),
);
const BuyerOrdersPage = lazy(() =>
  import(
    /* webpackChunkName: "buyer-orders" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.BuyerOrdersRedirect })),
);
const BuyerOrderDetailPage = lazy(() =>
  import(
    /* webpackChunkName: "buyer-order-detail" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.BuyerOrderDetail })),
);
const BuyerAccountPage = lazy(() =>
  import(
    /* webpackChunkName: "buyer-account" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.BuyerAccount })),
);
const BuyerSplitsReturnPage = lazy(
  () =>
    import(
      /* webpackChunkName: "buyer-splits-return" */ 'features/buyer-marketplace/buyer/splits-return'
    ),
);
const BuyerInterestUnsubscribePage = lazy(
  () =>
    import(
      /* webpackChunkName: "buyer-interest-unsubscribe" */ 'features/buyer-marketplace/buyer/interest-unsubscribe'
    ),
);
const NotificationPromptAnswerPage = lazy(
  () =>
    import(
      /* webpackChunkName: "notification-prompt-answer" */ 'features/notifications/notification-prompt-answer-page'
    ),
);
const BuyerProcessorPage = lazy(
  () =>
    import(
      /* webpackChunkName: "buyer-processor" */ 'features/buyer-marketplace/buyer/processor'
    ),
);
const ProducerProcessorsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "producer-processors" */ 'pages/producer/processors'
    ),
);
const ProducerAnimalsPage = lazy(() =>
  import(
    /* webpackChunkName: "producer-animals" */ 'pages/producer/animals'
  ).then((m) => ({ default: m.ProducerAnimalsPage })),
);
const ProducerCustomersPage = lazy(
  () =>
    import(
      /* webpackChunkName: "producer-customers" */ 'features/producer-customers'
    ),
);
const ProducerJobDetail = lazy(() =>
  import(
    /* webpackChunkName: "producer-job-detail" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.ProducerJobDetail })),
);
const ProducerInvoicePage = lazy(() =>
  import(
    /* webpackChunkName: "producer-invoice" */ 'features/producer-buyer-portal'
  ).then((m) => ({ default: m.ProducerInvoicePage })),
);
const ProfilePage = lazy(
  () => import(/* webpackChunkName: "profile" */ 'pages/profile'),
);
const PromosPage = lazy(
  () => import(/* webpackChunkName: "promos" */ 'pages/promos'),
);
const SchedulingDetailsHome = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-details" */ 'pages/scheduling/details'
    ),
);
const SchedulingEditCutsheets = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-edit-cutsheets" */ 'pages/scheduling/edit'
    ),
);
const ProcessingPartnerDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "processing-partner-detail" */ 'pages/scheduling/processor'
    ),
);
const VendorlessLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-landing" */ 'pages/scheduling/landing'
    ),
);
const SignupPage = lazy(
  () => import(/* webpackChunkName: "signup" */ 'pages/signup'),
);
const WaitlistUnsubscribe = lazy(
  () =>
    import(
      /* webpackChunkName: "waitlist-unsubscribe" */ 'pages/waitlist-unsubscribe'
    ),
);
const ViewInvoicePage = lazy(() =>
  import(
    /* webpackChunkName: "view-invoice" */ 'pages/processor/invoices/view-invoice-page'
  ).then((m) => ({ default: m.ViewInvoicePage })),
);
const ExportsPage = lazy(
  () => import(/* webpackChunkName: "exports" */ 'pages/processor/exports'),
);

const KioskPage = lazy(
  () => import(/* webpackChunkName: "kiosk" */ 'pages/kiosk'),
);
const NotificationsPage = lazy(() =>
  import(
    /* webpackChunkName: "notifications" */ 'pages/processor/notifications'
  ).then((m) => ({ default: m.NotificationsPage })),
);
const EditNotificationPage = lazy(() =>
  import(
    /* webpackChunkName: "edit-notification" */ 'pages/processor/notifications/edit-notification'
  ).then((m) => ({ default: m.EditNotificationPage })),
);
const PhoneNumberSettingsPage = lazy(() =>
  import(
    /* webpackChunkName: "phone-settings" */ 'pages/processor/scheduling-settings/_views/phone-number-settings'
  ).then((m) => ({ default: m.PhoneNumberSettings })),
);

// In test mode, clear stale Apollo persist cache from previous faker runs.
if (import.meta.env.VITE_PLAYWRIGHT_TEST_MODE === 'true') {
  for (const key of Object.keys(window.localStorage)) {
    if (key.startsWith('apollo-cache-persist')) {
      window.localStorage.removeItem(key);
    }
  }
}

// Links in our outbound SMS carry the recipient's phone number as `?p=`.
// Capture it into the remembered sign-in identifier and strip it from the URL
// before the router reads `location` — customers who tap an SMS link on a
// signed-out device otherwise register a second account instead of signing in.
captureSmsRecipientPhone();

// create root
const root = createRoot(document.getElementById('root') as HTMLElement);

// Wrapper component for lazy-loaded pages with Suspense
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<FullscreenLoading />}>{children}</Suspense>
);

// Redirect buyer-only users to their orders (e.g. from email cutsheet links)
const BuyerRedirectGuard = ({
  component: Component,
}: {
  component: ComponentType;
}) => {
  const { user } = useUserState();
  if (user && isBuyerOnly(user)) {
    return <Navigate to="/buyer/orders" replace />;
  }
  return <Component />;
};

// Self-corrects a user who opened the "wrong" version of a job detail page
const JobRoleRedirectGuard = ({
  component: Component,
  audience,
}: {
  component: ComponentType;
  audience: 'producer' | 'processor';
}) => {
  const { user } = useUserState();
  const { id, jobId } = useParams();
  const schedulingId = id ?? jobId;

  if (user && schedulingId) {
    if (audience === 'producer' && isProcessor(user)) {
      return (
        <Navigate to={`/processing-job/${schedulingId}/cutsheets`} replace />
      );
    }

    // Buyer-only users get the dedicated, buyer-scoped order page instead of the
    // producer job detail (which would expose every split/buyer on the animal).
    // Checked after the processor branch; cannot match producers or processors.
    if (audience === 'producer' && isBuyerOnly(user)) {
      return <Navigate to={`/buyer/orders/${schedulingId}`} replace />;
    }

    // isProducer() matches any Basic user — intentionally including buyers.
    // Mirror the processor redirect in reverse: a Basic user who lands on ANY
    // processing-job tab (details, cutsheets, …) is sent to their /job/:id
    // page. The Basic-allowed head/edit-split-type routes aren't wrapped by
    // this guard, so they remain reachable.
    if (audience === 'processor' && isProducer(user)) {
      return <Navigate to={`/job/${schedulingId}`} replace />;
    }
  }

  return <Component />;
};

// protected route component
const ProtectedRoute = ({
  component,
  allowedUserRoles,
  canAdminView = undefined,
  skipLazyWrapper = false,
}: {
  component: ComponentType;
  allowedUserRoles: EnumUserRole[];
  canAdminView?: boolean;
  skipLazyWrapper?: boolean;
}) => {
  const { user } = useUserState();
  const cognitoAuth = useContext(CognitoAuthContext);
  const location = useLocation();

  // Show loading while checking authentication
  if (cognitoAuth?.isLoading) {
    return <FullscreenLoading />;
  }

  // If not authenticated, redirect to login with current path for post-login redirect
  if (!cognitoAuth?.isAuthenticated) {
    const redirectPath = `${location.pathname}${location.search}`;
    // eslint-disable-next-line no-console
    console.log(
      '[ProtectedRoute] User not authenticated, redirecting to login with path:',
      redirectPath,
    );
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  const Component = component;

  // Ensure that VendorOwners can access Vendor routes. Copy rather than push
  // into the caller's array — allow-lists may be shared constants.
  const effectiveAllowedRoles = allowedUserRoles?.includes(EnumUserRole.Vendor)
    ? [...allowedUserRoles, EnumUserRole.VendorOwner]
    : allowedUserRoles;

  // if you pass in a list of allowedUserRoles and the user is not of those allowed roles.
  // Admins can access all things.
  if (
    user &&
    effectiveAllowedRoles &&
    !includes(effectiveAllowedRoles, user?.role) &&
    ((typeof canAdminView === 'undefined' &&
      user.role !== EnumUserRole.Admin) ||
      canAdminView === false)
  ) {
    return (
      <Error
        error={{ name: '404', message: 'Page not found.' }}
        logo={
          <img
            src={logo}
            height={100}
            alt="farmshare partners logo"
            className="mt-1 text-body p-3 p-md-0"
          />
        }
      />
    );
  }

  if (skipLazyWrapper) {
    return <Component />;
  }

  return (
    <LazyWrapper>
      <Component />
    </LazyWrapper>
  );
};

// Stable components so JobRoleRedirectGuard runs before ProtectedRoute's role
// check — otherwise a processor hitting the Basic-only /job/:id would 404
// before the guard could redirect them.
const ProducerJobDetailRoute = () => (
  <ProtectedRoute
    component={ProducerJobDetail}
    allowedUserRoles={[EnumUserRole.Basic]}
    canAdminView={false}
  />
);

const ProcessingJobRoute = () => (
  <ProtectedRoute
    allowedUserRoles={[EnumUserRole.Basic, ...PROCESSOR_VIEW_ROLES]}
    component={ProcessingJob}
  />
);

// Catch-all 404. Buyers can reach a dead URL via stale email deep-links; give
// them recovery links back into the portal so they aren't stranded with no nav.
const NotFoundRoute = () => {
  const { user } = useUserState();
  const isBuyer = !!user && isBuyerOnly(user);
  return (
    <>
      <Error
        error={{ name: '404', message: 'Page not found.' }}
        logo={
          <img
            src={logo}
            height={100}
            alt="farmshare partners logo"
            className="mt-1 text-body p-3 p-md-0"
          />
        }
      />
      {isBuyer && (
        <div className="position-fixed bottom-0 start-50 translate-middle-x mb-5 d-flex gap-3">
          <Link to="/buyer/orders" className="btn btn-primary">
            Your Orders
          </Link>
        </div>
      )}
    </>
  );
};

// Generate routes from all registered modules
const moduleRoutes = moduleRegistry.generateRoutes(
  ProtectedRoute,
  LazyWrapper,
  ModuleProtectedRoute,
);

const router = createBrowserRouter([
  {
    path: '/promos',
    element: (
      <>
        <PageViewTracker />
        <LazyWrapper>
          <PromosPage />
        </LazyWrapper>
      </>
    ),
  },
  {
    // The wild-game kiosk. Top-level and OUTSIDE the app shell on purpose: a
    // hunter standing in a lobby must not be looking at a processor's
    // navigation, and on a shared tablet that nav would be a way into the
    // plant's own data. Public, with no route guard — there is no session at a
    // kiosk, and the URL's token is what authorizes it.
    //
    // Apollo is still needed for the two public operations (they are on the
    // anonymous allowlist); the auth provider comes with it because the client
    // is built on top of one, and it is happy with no session.
    path: '/kiosk/:token',
    element: (
      <CognitoAuthProvider>
        <AuthApolloProvider>
          <PageViewTracker />
          <LazyWrapper>
            <KioskPage />
          </LazyWrapper>
        </AuthApolloProvider>
      </CognitoAuthProvider>
    ),
  },
  {
    path: '/login',
    element: (
      <CognitoAuthProvider>
        <SettingsProvider>
          <PageViewTracker />
          <LazyWrapper>
            <CognitoLoginPage />
          </LazyWrapper>
        </SettingsProvider>
      </CognitoAuthProvider>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <CognitoAuthProvider>
        <SettingsProvider>
          <PageViewTracker />
          <LazyWrapper>
            <AuthCallbackPage />
          </LazyWrapper>
        </SettingsProvider>
      </CognitoAuthProvider>
    ),
  },
  {
    element: (
      <CognitoAuthProvider>
        <ToastrProvider>
          <AuthApolloProvider>
            <SettingsProvider>
              <ModalProvider onAnalyticsEvent={trackModalEvent}>
                <App />
              </ModalProvider>
            </SettingsProvider>
          </AuthApolloProvider>
        </ToastrProvider>
      </CognitoAuthProvider>
    ),
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomePage />
          </LazyWrapper>
        ),
      },
      {
        // Retired — `/explore` rendered the same producers and processors as the
        // marketplace carousels.
        path: '/explore',
        element: <Navigate to="/marketplace" replace />,
      },
      {
        // Retired — the public processor map is now the marketplace's
        // `?mode=process` half rather than a page of its own.
        path: '/find-a-processor',
        element: <Navigate to="/marketplace?mode=process" replace />,
      },
      {
        // The processor finder. This used to share the page with the buyer
        // marketplace (`?mode=buy`); with the marketplace switched off
        // (PRO-784) every mode lands here. Public — guests and producers.
        // Full-bleed (FULLSCREEN_ROUTE_PREFIXES).
        path: '/marketplace',
        element: (
          <LazyWrapper>
            <ProcessFinderPage showAudienceToggle={false} />
          </LazyWrapper>
        ),
      },
      {
        // Processor finder results (map + list), driven by URL query params.
        path: '/marketplace/search',
        element: (
          <LazyWrapper>
            <ProcessSearchPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/signup',
        element: (
          <LazyWrapper>
            <SignupPage />
          </LazyWrapper>
        ),
      },
      {
        // Index-only redirect; all /scheduling/:... detail routes are defined
        // below and still match (React Router exact-matches static paths first).
        // The legacy directory this served now lives in the marketplace's
        // process mode.
        path: '/scheduling',
        element: <Navigate to="/marketplace?mode=process" replace />,
      },
      {
        path: '/buyer/find',
        element: <Navigate to="/marketplace?mode=process" replace />,
      },
      {
        path: '/faq',
        element: (
          <LazyWrapper>
            <FAQPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/compare',
        element: (
          <LazyWrapper>
            <ComparisonTool />
          </LazyWrapper>
        ),
      },
      {
        // Vendorless directory placeholders. Defined before `/scheduling/:slug`
        // so the literal `landing` segment is matched as a route, not a slug.
        path: '/scheduling/landing/:landingPageId',
        element: (
          <LazyWrapper>
            <VendorlessLandingPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:slug',
        element: (
          <LazyWrapper>
            <ProcessingPartnerDetail />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:schedulingId/details',
        element: (
          <LazyWrapper>
            <SchedulingDetailsHome />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:schedulingId/cutsheets',
        element: (
          <ProtectedRoute
            component={() => (
              <BuyerRedirectGuard component={SchedulingEditCutsheets} />
            )}
            allowedUserRoles={[EnumUserRole.Basic, EnumUserRole.Vendor]}
          />
        ),
      },
      ...moduleRoutes,
      {
        path: '/admin',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Admin]}
            component={AdminPage}
          />
        ),
        children: [
          {
            path: '/admin/job-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminJobExportPage}
              />
            ),
          },
          {
            path: '/admin/hanging-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminHangingWeightExportPage}
              />
            ),
          },
          {
            path: '/admin/processor-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminProcessorExportPage}
              />
            ),
          },
          {
            path: '/admin/assets',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminAssetPage}
              />
            ),
          },
          {
            path: '/admin/shipments',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminShipmentPage}
              />
            ),
          },
          {
            path: '/admin/tenants',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminTenantPage}
              />
            ),
          },
          {
            path: '/admin/users',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminUsersPage}
              />
            ),
          },
          {
            path: '/admin/tenants/:id',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminTenantDetailsPage}
              />
            ),
          },
          {
            path: '/admin/stats',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminStatsPage}
              />
            ),
          },
          {
            path: '/admin/account-activity',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminAccountActivityPage}
              />
            ),
          },
          {
            path: '/admin/audit-trail',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminAuditTrailPage}
              />
            ),
          },
          {
            path: '/admin/notification-audit',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminNotificationAuditPage}
              />
            ),
          },
          {
            path: '/admin/marketplace-leads',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminMarketplaceLeadsPage}
              />
            ),
          },
          {
            path: '/admin/landing-pages',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminLandingPagesPage}
              />
            ),
          },
          {
            path: '/admin/landing-pages/:id',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminLandingPageDetailsPage}
              />
            ),
          },
          {
            path: '/admin/cutsheet-libraries',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminCutsheetLibrariesPage}
              />
            ),
          },
        ],
      },
      {
        path: '/integrations',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={IntegrationsPage}
          />
        ),
      },
      {
        path: '/exports',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={ExportsPage}
          />
        ),
      },
      {
        path: '/profile',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Basic, ...PROCESSOR_VIEW_ROLES]}
            component={ProfilePage}
          />
        ),
      },
      {
        path: 'processor/notifications',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={NotificationsPage}
          />
        ),
      },
      {
        path: 'processor/notifications/:id',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={EditNotificationPage}
          />
        ),
      },
      {
        path: 'processor/phone-settings',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={PhoneNumberSettingsPage}
          />
        ),
      },
      {
        path: 'processor/settings/waitlist',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={Waitlist}
          />
        ),
      },
      {
        path: '/waitlist/unsubscribe/:token',
        element: (
          <LazyWrapper>
            <WaitlistUnsubscribe />
          </LazyWrapper>
        ),
      },
      {
        path: '/processor/:tab?/:subTab?',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={ProcessorAdminPage}
            skipLazyWrapper
          />
        ),
      },
      {
        path: '/processor/customers/:id',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={ProcessorCustomerDetailsPage}
          />
        ),
      },
      {
        path: '/processor/invoices/:invoiceId',
        element: (
          <ProtectedRoute
            allowedUserRoles={PROCESSOR_VIEW_ROLES}
            component={ViewInvoicePage}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/generate-invoice',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={GenerateInvoice}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/:tab?',
        element: (
          <JobRoleRedirectGuard
            audience="processor"
            component={ProcessingJobRoute}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/:animalHeadId?',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Basic, ...PROCESSOR_VIEW_ROLES]}
            component={AnimalHead}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/add',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={AddAnimalHead}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/:animalHeadId/edit-split-type',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor, EnumUserRole.Basic]}
            component={EditSplitType}
          />
        ),
      },
      {
        path: '/job/:id',
        element: (
          <JobRoleRedirectGuard
            audience="producer"
            component={ProducerJobDetailRoute}
          />
        ),
      },
      {
        // An invoice billed to the signed-in user, opened from the task
        // list's Invoices tab.
        path: '/invoice/:id',
        element: (
          <ProtectedRoute
            component={ProducerInvoicePage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/jobs',
        element: (
          <ProtectedRoute
            component={ProducerJobsPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute
            component={ProducerDashboard}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/buyer',
        element: <Navigate to="/buyer/orders" replace />,
      },
      {
        path: '/buyer/orders',
        element: (
          <ProtectedRoute
            component={BuyerOrdersPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/buyer/orders/:jobId',
        element: (
          <ProtectedRoute
            component={BuyerOrderDetailPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/buyer/account',
        element: (
          <ProtectedRoute
            component={BuyerAccountPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        // Retired with the buyer marketplace (PRO-784). Home routes each
        // visitor on: guests to the front door, buyers to their orders.
        path: '/buyer/available',
        element: <Navigate to="/" replace />,
      },
      {
        // Retired with the buyer marketplace (PRO-784): deposit checkout. Old
        // links and emails land on home.
        path: '/buyer/checkout/:splitId',
        element: <Navigate to="/" replace />,
      },
      {
        // Retired with the buyer marketplace (PRO-784): the buyer animal page
        // and its receipt. Before the marketplace no customer had an animal
        // page; orders open on the order page instead.
        path: '/buyer/animal/:animalId',
        element: <Navigate to="/buyer/orders" replace />,
      },
      {
        path: '/buyer/animal/:animalId/receipt',
        element: <Navigate to="/buyer/orders" replace />,
      },
      {
        // Public — where a confirmable notification email's Confirm / Can't
        // make it links land. Tokenized, no login (LazyWrapper only).
        path: '/notification-prompt/:token',
        element: (
          <LazyWrapper>
            <NotificationPromptAnswerPage />
          </LazyWrapper>
        ),
      },
      {
        // Public — tokenized, no-login unsubscribe for marketplace-interest
        // emails (Notifications Phase 9). Mirrors the /waitlist/unsubscribe/:token
        // route's shape (LazyWrapper only, no ProtectedRoute).
        path: '/buyer/interest/unsubscribe/:token',
        element: (
          <LazyWrapper>
            <BuyerInterestUnsubscribePage />
          </LazyWrapper>
        ),
      },
      {
        // Canonical public URL for the per-processor "Available Splits"
        // storefront (T7.2). SEO authority consolidates here; /buyer/processor/:slug
        // remains as an alias (alias entry below). T7.3 adds <link rel="canonical">.
        path: '/processors/:slug',
        element: (
          <LazyWrapper>
            <BuyerProcessorPage />
          </LazyWrapper>
        ),
      },
      {
        // Alias — kept so old bookmarks and internal links still resolve.
        // New in-app navigations use /processors/:slug (canonical).
        path: '/buyer/processor/:slug',
        element: (
          <LazyWrapper>
            <BuyerProcessorPage />
          </LazyWrapper>
        ),
      },
      {
        // Retired with the buyer marketplace (PRO-784): the producer's public
        // storefront.
        path: '/producer/:slug',
        element: <Navigate to="/" replace />,
      },
      {
        // Retired — transactions content is folding into /buyer/orders
        // (Task 6). features/buyer-marketplace/buyer/transactions.tsx is preserved on disk
        // (de-linked, not deleted) but no longer routed to.
        path: '/buyer/transactions',
        element: (
          <ProtectedRoute
            component={() => <Navigate to="/buyer/orders" replace />}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        // Retired with the buyer marketplace (PRO-784).
        path: '/buyer/cart',
        element: <Navigate to="/buyer/orders" replace />,
      },
      {
        // Kept while the marketplace is switched off (PRO-784): a hosted
        // Stripe deposit checkout started before the switch-off still lands
        // here, and this page confirms it (buyerSyncDepositCheckout) before
        // moving on. Remove once no checkout can still be in flight.
        path: '/buyer/splits/return',
        element: (
          <ProtectedRoute
            component={BuyerSplitsReturnPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/my-processor',
        element: (
          <ProtectedRoute
            component={ProducerProcessorsPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/my-animals',
        element: (
          <ProtectedRoute
            component={ProducerAnimalsPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/customers',
        element: (
          <ProtectedRoute
            component={ProducerCustomersPage}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      // Producer IA reshape 2026-07-10: /selling retired. Setup (eligibility,
      // Stripe payments, default deposits) lives on /profile; the buyer
      // roster became the person-centric /customers page. Redirects keep
      // bookmarks and emailed links working.
      {
        path: '/selling',
        element: <Navigate to="/profile" replace />,
      },
      {
        path: '/selling/roster',
        element: <Navigate to="/customers" replace />,
      },

      {
        path: ':userId?/chat/:id?',
        element: (
          <LazyWrapper>
            <ChatPage />
          </LazyWrapper>
        ),
      },
      // Fallback route for 404s
      {
        path: '*',
        element: <NotFoundRoute />,
      },
      // {
      //   path: '/processor/floor/:tab?/:date?',
      //   element: (
      //     <ProtectedRoute
      //       allowedUserRoles={[EnumUserRole.Vendor]}
      //       component={ProcessingFloorPage}
      //     />
      //   ),
      // },
    ],
  },
]);

// Service worker will be initialized automatically via the enhanced hook

// Styles go in through the CSSOM (`insertRule`) in every build, not one
// `<style>` element per rule.
//
// Emotion only does that by default in a production build. In development —
// `yarn dev`, and any stage not built for production — each rule is its own
// tag, and MUI adds four more ahead of all the others for every text input on
// the page. Inserting a tag in front of thousands of siblings is slow in the
// browser itself: opening Settings (192 inputs) after the calendar spent
// three seconds of one frozen frame doing nothing else.
//
// The cost is that rules no longer show as text inside the `<style>` tags in
// the Elements panel. They are still listed, and editable, in Styles.
const emotionCache = createCache({ key: 'css', speedy: true });

// render to root
root.render(
  <StrictMode>
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        {/* Base document title. Pages that set their own <title> (e.g. the
          producer/processor storefronts) override this while mounted; when
          they unmount, react-helmet-async falls back to this defaultTitle so
          navigating to a page without its own <title> restores "Farmshare"
          instead of leaving the previous page's title stuck in the tab. */}
        <Helmet defaultTitle="Farmshare" />
        <RecoilRoot>
          <UserStateProvider>
            <MuiSwitchableThemeProvider>
              <RouterProvider router={router} />
            </MuiSwitchableThemeProvider>
          </UserStateProvider>
        </RecoilRoot>
      </HelmetProvider>
    </CacheProvider>
  </StrictMode>,
);
